import { X, Download, Loader2, Bot, User, Settings } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui';
import { api, getLogStreamUrl } from '@/api/client';
import type { LogEntry } from '@/types';

function extractLogContent(log: LogEntry): string {
  if (log.msg) return log.msg;

  if (log.type === 'sdk' && log.metadata) {
    const content = log.metadata.content as Array<{ text?: string; name?: string; input?: unknown }> | undefined;
    if (content && Array.isArray(content)) {
      const textContent = content.find((c) => c.text);
      if (textContent?.text) return textContent.text;

      const toolContent = content.find((c) => c.name);
      if (toolContent?.name) {
        return `→ ${toolContent.name}`;
      }
    }

    const firstContent = content?.[0] as { tool_use_id?: string; content?: string } | undefined;
    if (firstContent?.tool_use_id && firstContent?.content) {
      const preview = firstContent.content.slice(0, 200);
      return preview + (firstContent.content.length > 200 ? '...' : '');
    }

    if (log.event === 'SystemMessage') {
      const subtype = log.metadata.subtype as string | undefined;
      return subtype ? `[${subtype}]` : '[system]';
    }
  }

  return log.event || '';
}

function formatLogForDownload(log: LogEntry): string {
  const ts = log.ts;
  const type = log.type;
  const event = log.event ? ` [${log.event}]` : '';
  const content = extractLogContent(log);

  if (log.type === 'sdk' && log.metadata) {
    return `[${ts}] [${type}]${event} ${content}\n    metadata: ${JSON.stringify(log.metadata)}`;
  }

  return `[${ts}] [${type}]${event} ${content}`;
}

export function LogViewer() {
  const { logViewerOpen, logViewerRunId, closeLogViewer } = useUIStore();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  useEffect(() => {
    if (!logViewerOpen || !logViewerRunId) return;

    let cancelled = false;

    async function fetchLogs() {
      try {
        setError(null);
        setLogs([]);

        const result = await api.runs.getLogs(logViewerRunId!);
        if (!cancelled) {
          setLogs(result.logs);
        }

        const runResult = await api.runs.get(logViewerRunId!);
        const run = runResult.run;

        if (run.status === 'pending' || run.status === 'running') {
          if (!cancelled) {
            setIsStreaming(true);
            startStreaming();
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load logs');
        }
      }
    }

    function startStreaming() {
      const url = getLogStreamUrl(logViewerRunId!);
      const eventSource = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = eventSource;

      eventSource.addEventListener('log', (event) => {
        try {
          const entry = JSON.parse(event.data) as LogEntry;
          setLogs((prev) => [...prev, entry]);
        } catch {
        }
      });

      eventSource.addEventListener('message', (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'connected') {
            console.log('SSE connected');
          }
        } catch {
        }
      });

      eventSource.onerror = () => {
        setIsStreaming(false);
        eventSource.close();
        eventSourceRef.current = null;
      };
    }

    fetchLogs();

    return () => {
      cancelled = true;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsStreaming(false);
    };
  }, [logViewerOpen, logViewerRunId]);

  const handleClose = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setLogs([]);
    setIsStreaming(false);
    setError(null);
    closeLogViewer();
  }, [closeLogViewer]);

  const handleDownload = useCallback(() => {
    const content = logs.map(formatLogForDownload).join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${logViewerRunId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [logs, logViewerRunId]);

  if (!logViewerOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={handleClose} />

      <div className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-2xl flex-col border-l border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-black">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-slate-900 dark:text-white">Logs</h2>
            {isStreaming && (
              <span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900 dark:text-green-300">
                <Loader2 className="h-3 w-3 animate-spin" />
                Live
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              disabled={logs.length === 0}
              className="rounded-button p-2 text-slate-500 hover:bg-surface-hover hover:text-slate-700 disabled:opacity-50 dark:hover:bg-surface-hover-dark dark:hover:text-slate-300"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={handleClose}
              className="rounded-button p-2 text-slate-500 hover:bg-surface-hover hover:text-slate-700 dark:hover:bg-surface-hover-dark dark:hover:text-slate-300"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="scrollbar-thin flex-1 overflow-y-auto bg-slate-950 p-4 font-mono text-sm">
          {error ? (
            <p className="text-red-400">{error}</p>
          ) : logs.length === 0 ? (
            <p className="text-slate-500">No logs yet...</p>
          ) : (
            logs.map((log, i) => (
              <div key={i} className="flex gap-2 py-0.5">
                <span className="flex-shrink-0 text-slate-500">
                  {new Date(log.ts).toLocaleTimeString()}
                </span>
                <span
                  className={cn(
                    'flex-shrink-0 w-12',
                    log.type === 'stderr' && 'text-red-400',
                    log.type === 'error' && 'text-red-400',
                    log.type === 'stdout' && 'text-slate-300',
                    log.type === 'app' && 'text-slate-300',
                    log.type === 'sdk' && 'text-blue-400',
                    log.type === 'system' && 'text-yellow-400'
                  )}
                >
                  [{log.type}]
                </span>
                {log.type === 'sdk' && log.event && (
                  <span
                    className={cn(
                      'flex-shrink-0 flex items-center gap-1',
                      log.event === 'AssistantMessage' && 'text-purple-400',
                      log.event === 'UserMessage' && 'text-green-400',
                      log.event === 'SystemMessage' && 'text-yellow-400'
                    )}
                  >
                    {log.event === 'AssistantMessage' && <Bot className="h-3 w-3" />}
                    {log.event === 'UserMessage' && <User className="h-3 w-3" />}
                    {log.event === 'SystemMessage' && <Settings className="h-3 w-3" />}
                  </span>
                )}
                <span className="text-slate-200 whitespace-pre-wrap break-all">
                  {extractLogContent(log)}
                </span>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </div>
    </>
  );
}
